import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { ColumnSource, RequestFilters, RowSource, CellSource, CellModel } from "@omegagrid/grid";
import { ToolbarItem } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { DimensionDefinition, StatementRowAcct, StatementDefinition } from "@reporting/rp-types/definition";
import { MODULE_CODES } from "@reporting/rp-types/accounting";
import { FinDropdownAdapter, DimTreeAdapter } from "../../adapters";

const PAGE_SIZE = 500;

export class FinAccountsGridEditorAdapter extends GridEditorAdapter {
	private _columns: ColumnSource[];
	private dims: DimensionDefinition[];
	private a: number;
	private editable: boolean = true;

	constructor (
		private statement: StatementDefinition,
		private year: number,
	) {
		super();
		this.a = statement.accounting;
		this.editable = this.a === rp.accountingProvider.a;
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 3,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow
			}, {
				action: 'new',
				title: msg('New'),
				icon: 'plus',
				color: ColorTypes.Blue
			}],
			filter: false,
			sorting: false,
			hidden: !this.editable,
		}, {
			data: '_id_account',
			header: '#',
			t: 't',
			e: false,
			w: 100,
			filter: false
		}, {
			data: 'id_account',
			header: msg('Account'),
			t: 's',
			validation: { allowInvalid: true },
			w: 300,
			filter: true,
			adapter:  Object.assign(new FinDropdownAdapter('accounts'), { accounting: this.a })
		}, {
			data: 'id_row_parent',
			header: msg('Row'),
			t: 's',
			validation: { allowInvalid: true },
			w: 300,
			filter: true,
			adapter:  Object.assign(new FinDropdownAdapter('rows', this.statement.id_statement.toString()), { accounting: this.a })
		}];
		this.dims = await rp.ms('definition').get<DimensionDefinition[]>(`/visibleDimensions/${MODULE_CODES.FINANCE}`, { accounting: this.a });
		this.dims.forEach(dim => {
			this._columns.push({
				data: `id_dim_${dim.dim_num}`,
				header: dim.description,
				t: 's',
				w: 160,
				filter: true,
				adapter: Object.assign(new DimTreeAdapter(MODULE_CODES.FINANCE, dim.dim_num, { selectMode: 'single', autoClose: true }), { accounting: this.a }),
			});
		});
		this._columns.push({
			data: 'coef',
			header: msg('Coefficient'),
			t: 'n',
			w: 80,
			filter: true,
		}, {
			data: 'who_created',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 180,
			filter: true,
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145,
			filter: true,
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true
		});
		return this._columns;
	}

	private createCell(rc: StatementRowAcct, col: ColumnSource): Partial<CellSource> | null {
		if (col.hidden) return null;
		switch (col.data as string) {
			case '_id_account':
				return { v: rc.id_account };
			case 'id_account':
				return { v: rc.id_account, dv: `${rc.account || rc.id_account}` };
			case 'id_row_parent':
				return { v: rc.id_row_parent, dv: rc.row_name };
		}
		if ((col.data as string)?.startsWith('id_dim_')) {
			const dimNum = (col.data as string).split('_')[2];
			return { v: rc[`id_dim_${dimNum}` as keyof StatementRowAcct], dv: rc[`dim_${dimNum}` as keyof StatementRowAcct] as string };
		}
		return null;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const accounts = await rp.ms('definition').get<StatementRowAcct[]>(`/statementAccounts`, this.createRequestParams(filters, {
			idStatement: this.statement.id_statement,
			year: this.year,
			count: PAGE_SIZE,
			accounting: this.a,
		}));
		const rows: RowSource[] = [];
		for (const rc of accounts) {
			const rs: RowSource = { data: rc };
			for (const col of this._columns) {
				const cell = this.createCell(rc, col);
				if (cell) rs.data[col.data] = cell;
			}
			rows.push(rs);
		}
		return rows;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			editable: this.editable,
			toolbar: {
				save: { enabled: this.editable },
				discard: { enabled: this.editable },
				new: { enabled: this.editable }
			},
			customToolbar: this.editable ? [{
				type: 'button',
				id: 'accountsDelete',
				text: msg('Accounts deletion'),
				icon: 'trash-list',
				color: ColorTypes.Gray
			}] as ToolbarItem[] : undefined,
			showEmptyRow: this.editable,
			rowMenu: {
				addRows: this.editable,
				deleteRows: this.editable,
			},
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index, year: this.year}, row.data));
		await rp.ms('definition').post(`/statementAccounts`, data);
	}

	onAction(action: string, cell: CellModel): void {
		switch (action) {
			case 'discard':
				this.editor.refresh();
				break;
			case 'accountsDelete':
				rp.host.openModalSidebar({
					id: `definition-finAccountsDelete-${this.statement.id_statement}-${this.year}`,
					caption: `${msg('Accounts deletion')} - ${this.statement.statement}`
				});
				break;
		}
	}

}
